import { createStaticMapWithLatLng } from '@/commonFunctions/utils'
import { Email, Navigation, Phone, UserIconFilled } from '@/components/icons'
import { useGetClubStaff } from '@/services'
import { Club, ClubStaff } from '@/types'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { useMediaQuery, useWindowSize } from 'usehooks-ts'

type Props = {
  club: Club
  isPage?: boolean
}

export const ClubInfo = ({ club, isPage }: Props) => {
  const { t } = useTranslation()

  const params = useParams()
  const { clubId } = params

  const { mutate: getClubStaff, data: resGetClubStaff } = useGetClubStaff()

  const isMobile = useMediaQuery('(max-width: 767px)')
  const { width } = useWindowSize()

  const [aboutHtml, setAboutHtml] = useState('')
  const [staffs, setStaffs] = useState<ClubStaff[]>([])

  const redirectToMap = (latitude?: number, longitude?: number) => {
    if (latitude && longitude) {
      const url = `https://www.google.com/maps?q=${latitude},${longitude}`
      window.open(url, '_blank')
    }
  }

  const onGetData = async () => {
    if (clubId) {
      getClubStaff(Number(clubId))
    }
  }

  useEffect(() => {
    onGetData()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clubId])

  useEffect(() => {
    if (club) setAboutHtml(`${club?.longDescription?.content ? club?.longDescription?.content : ''}`)
  }, [club])

  useEffect(() => {
    if (resGetClubStaff) {
      const directors = resGetClubStaff.result.directors.map((item) => {
        return { ...item, isDirectors: true }
      })
      const organizers = resGetClubStaff.result.organizers

      setStaffs([...directors, ...organizers])
    }
  }, [resGetClubStaff])

  return (
    <div className={`flex-col bg-transparent ${isPage ? 'w-full flex' : 'hidden md:flex w-1/3'}`}>
      <div className="bg-white rounded-2xl">
        <img
          src={createStaticMapWithLatLng({
            w: isMobile ? 400 : width / 5,
            lat: club?.address?.latitude,
            lng: club?.address?.longitude,
          })}
          className="w-full rounded-t-2xl"
          alt=""
        />

        <div className="flex flex-col p-4 gap-y-1">
          <p className="text-navy-900 text-opacity-80 font-semibold text-base max-w-[192px] md:w-full">
            {club?.clubName}
          </p>
          <span className="text-navy-900 text-opacity-50 text-sm">{club?.shortAddress}</span>
          <button
            className="flex bg-primary-500 items-center py-2 px-4 rounded-full gap-x-1 text-white text-sm font-semibold w-28 my-2"
            onClick={() => redirectToMap(club?.address?.latitude, club?.address?.longitude)}
          >
            <Navigation className="text-white" />
            {t('common.navigate')}
          </button>
        </div>

        <div className="flex flex-col p-4 gap-y-2">
          <h3 className="text-xl font-semibold text-navy-900">{t('club.clubContact')}</h3>
          <div className="flex flex-col gap-y-2">
            {club?.attributes?.contacts?.children?.email?.value && (
              <div className="flex gap-x-1 items-center">
                <Email className="text-navy-900" />
                <span className="text-sm text-navy-900 break-all">
                  {club?.attributes?.contacts?.children?.email?.value}
                </span>
              </div>
            )}
            {club?.attributes?.contacts?.children?.phone?.value && (
              <div className="flex gap-x-1 items-center">
                <Phone className="text-navy-900" />
                <span className="text-sm text-navy-900 break-all">
                  {club?.attributes?.contacts?.children?.phone?.value}
                </span>
              </div>
            )}
          </div>
        </div>

        <div className="flex flex-col p-4 gap-y-2">
          <h3 className="text-xl font-semibold text-navy-900">{t('club.clubAbout')}</h3>
          <div
            className="text-navy-900 text-opacity-60 text-sm break-words"
            dangerouslySetInnerHTML={{ __html: aboutHtml }}
          />
        </div>

        <div className="flex flex-col p-4 gap-y-2">
          <h3 className="text-xl font-semibold text-navy-900">{t('club.directors')}</h3>
          {staffs.map((item) => (
            <React.Fragment key={item?.userId}>
              {item?.isDirectors ? (
                <div className="flex justify-between bg-white rounded-2xl py-2">
                  <div className="flex gap-x-2">
                    {item?.mediaUrl ? (
                      <img src={item?.mediaUrl} className="rounded-full w-10 h-10" alt="" />
                    ) : (
                      <UserIconFilled className="w-10 h-10 text-primary-500" />
                    )}
                    <div className="flex flex-col flex-1">
                      <p className="text-sm font-semibold text-navy-900">{item?.name}</p>
                      <p className="text-sm text-navy-900 text-opacity-80 break-all">{item?.email}</p>
                      <p className="text-xs text-navy-900 text-opacity-60 break-all">{item?.phone}</p>
                    </div>
                  </div>
                </div>
              ) : (
                <></>
              )}
            </React.Fragment>
          ))}
        </div>

        <div className="flex flex-col p-4 gap-y-2">
          <h3 className="text-xl font-semibold text-navy-900">{t('club.organizers')}</h3>
          {staffs.map((item) => (
            <React.Fragment key={item?.userId}>
              {!item?.isDirectors ? (
                <div className="flex justify-between bg-white rounded-2xl py-2">
                  <div className="flex gap-x-2">
                    {item?.mediaUrl ? (
                      <img src={item?.mediaUrl} className="rounded-full w-10 h-10" alt="" />
                    ) : (
                      <UserIconFilled className="w-10 h-10 text-primary-500" />
                    )}
                    <div className="flex flex-col flex-1">
                      <p className="text-sm font-semibold text-navy-900">{item?.name}</p>
                      <p className="text-sm text-navy-900 text-opacity-80 break-all">{item?.email}</p>
                      <p className="text-xs text-navy-900 text-opacity-60 break-all">{item?.phone}</p>
                    </div>
                  </div>
                </div>
              ) : (
                <></>
              )}
            </React.Fragment>
          ))}
        </div>
      </div>
    </div>
  )
}
